import axios from 'axios'
import env from './../../environments/environment'
import AuthService from './Auth.service'
import { attachAppCheck } from '../../api/FirebaseAuth'

// The recruiting_meeting Firebase function lives next to the main `api`
// function, so we derive its base URL by swapping the function name in
// env.api.base_url. Same pattern works in local, beta, and production.
const buildResourceBase = resource => {
  const base = env.api && env.api.base_url ? env.api.base_url : ''
  return base.replace(/\/api\/?$/, `/${resource}/`)
}

const RECRUITING_BASE = buildResourceBase('recruiting_meeting')
const USERS_BASE = buildResourceBase('users')

const publicInstance = () =>
  attachAppCheck(
    axios.create({
      baseURL: RECRUITING_BASE,
      timeout: 30000,
      headers: { 'Content-Type': 'application/json' },
    })
  )

const authedInstance = baseURL =>
  attachAppCheck(
    axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${AuthService.getToken() || ''}`,
      },
    })
  )

const verifyIdentity = async identifier => {
  const res = await publicInstance().post('/verify', { identifier })
  return res.data
}

const fetchLeaderSession = async () => {
  const res = await authedInstance(RECRUITING_BASE).get('/leader-session')
  return res.data
}

const setRecruitingPermission = async (userId, enabled) => {
  const res = await authedInstance(USERS_BASE).post(`/${parseInt(userId, 10)}/recruiting-permission`, {
    enabled: !!enabled,
  })
  return res.data
}

// Read `{ enabled, viewerCanEdit }` for a target user. Backed by
// GET /users/:id/recruiting-permission. Drives the AccountProfile permitted
// toggle's initial state and its visibility gate.
const fetchRecruitingPermission = async userId => {
  const res = await authedInstance(USERS_BASE).get(`/${parseInt(userId, 10)}/recruiting-permission`)
  return res.data
}

// Read the recruiting_meetings row tied to a calendar_event for the
// EventViewer Recruiter pickers. Returns recruiter_id_1/_2, resolved names,
// is_recruiting_calendar (frontend uses to gate render), and the viewer's
// edit authority. `calendarEventIdRaw` may be the int DB id or the Google
// event_id string; the backend resolves either.
const fetchEventRecruiters = async calendarEventIdRaw => {
  const res = await authedInstance(RECRUITING_BASE).get(`/event/${encodeURIComponent(calendarEventIdRaw)}/recruiters`)
  return res.data
}

// PATCH the recruiting_meetings row's recruiter slots. Null/empty values
// clear the slot. Returns the saved ids on success; 403 if viewer is not
// authorized (BST always; permitted users only before event start).
const setEventRecruiters = async (calendarEventIdRaw, { recruiter_id_1, recruiter_id_2 } = {}) => {
  const res = await authedInstance(RECRUITING_BASE).patch(
    `/event/${encodeURIComponent(calendarEventIdRaw)}/recruiters`,
    { recruiter_id_1, recruiter_id_2 }
  )
  return res.data
}

const fetchEventRecruiterHistory = async calendarEventIdRaw => {
  const res = await authedInstance(RECRUITING_BASE).get(
    `/event/${encodeURIComponent(calendarEventIdRaw)}/recruiter-history`
  )
  return res.data
}

// Pool of users eligible to be assigned as recruiters: the
// system_settings.recruiting---group-recruiting-permitted array, joined
// against the users table. Drives the TypeAhead options in the EventViewer
// Recruiter pickers.
const fetchPermittedRecruiters = async () => {
  const res = await authedInstance(RECRUITING_BASE).get('/permitted-recruiters')
  return res.data
}

const RecruitingMeetingService = {
  verifyIdentity,
  fetchLeaderSession,
  setRecruitingPermission,
  fetchRecruitingPermission,
  fetchEventRecruiters,
  setEventRecruiters,
  fetchEventRecruiterHistory,
  fetchPermittedRecruiters,
}

export default RecruitingMeetingService
